/**
 * Local-economy character catalog (not sim content: unlocks never affect gameplay determinism, so this is NOT
 * hashed into SIM_CONTENT_HASH). Mirrors the shape of the future server `catalog` table
 * (/workspace/handegg-accounts-spec.md §2: `sku, kind, price_coins, coins_granted, active`) so a server-authoritative
 * wallet can slot in later without changing this interface — see `packages/shared/src/wallet.ts`.
 */
import type { CharacterId } from './characters';
import { deepFreeze } from './freeze';

export const Rarity = { COMMON: 0, RARE: 1, EPIC: 2, LEGENDARY: 3 } as const;
export type Rarity = (typeof Rarity)[keyof typeof Rarity];
export const RARITY_NAMES: Readonly<Record<Rarity, string>> = {
  0: 'COMMON',
  1: 'RARE',
  2: 'EPIC',
  3: 'LEGENDARY',
};

export interface CatalogEntry {
  readonly rarity: Rarity;
  /** Coin price to unlock. 0 = unlocked for everyone from the start (the launch roster). */
  readonly priceCoins: number;
}

/**
 * Prices per handegg-roster.md "Pricing → Unlock cost by rarity": Common 300, Rare 1,500, Epic 4,000,
 * Legendary 12,000. The launch four (rookie/bigtex/zorg/grandpa) are free.
 */
export const CHARACTER_CATALOG: Readonly<Record<CharacterId, CatalogEntry>> = deepFreeze({
  rookie: { rarity: Rarity.COMMON, priceCoins: 0 },
  bigtex: { rarity: Rarity.COMMON, priceCoins: 0 },
  zorg: { rarity: Rarity.COMMON, priceCoins: 0 },
  grandpa: { rarity: Rarity.COMMON, priceCoins: 0 },
  dome: { rarity: Rarity.COMMON, priceCoins: 300 },
  turbo: { rarity: Rarity.RARE, priceCoins: 1500 },
  hopper: { rarity: Rarity.RARE, priceCoins: 1500 },
  rumble: { rarity: Rarity.EPIC, priceCoins: 4000 },
  lodestone: { rarity: Rarity.LEGENDARY, priceCoins: 12000 },
});

export const catalogFor = (id: CharacterId): CatalogEntry => CHARACTER_CATALOG[id];
/** A character that costs nothing: unlocked for every wallet from the start. */
export const isStarterCharacter = (id: CharacterId): boolean => catalogFor(id).priceCoins === 0;

// ── Ball skins (handegg-roster.md "Ball skins rule") ──────────────────────────────────────────────────────
/**
 * Purely cosmetic: never read by the sim (no entry here is hashed into SIM_CONTENT_HASH, no skin id is ever
 * stored in SimState), only by the renderer — which paints the skin of whoever has the ball, swapping on every
 * change of possession. `classic` is the default: every wallet owns it and it is what an unskinned ball renders.
 */
export const BALL_SKIN_IDS = [
  'classic',
  'bubblegum',
  'frostbite',
  'gold',
  'galaxy',
  'midnight',
  'voltage',
  'inferno',
  'starfall',
  'egg',
] as const;
export type BallSkinId = (typeof BALL_SKIN_IDS)[number];

export interface BallSkinEntry {
  readonly name: string;
  readonly rarity: Rarity;
  readonly priceCoins: number;
}

/** design/exports/store-balls.png (approved mockup: pricing and names below match it). THE EGG is the legendary
 *  chase skin (wobble idle + 4 crack stages, design/exports/balls-sheet.png). */
export const BALL_SKIN_CATALOG: Readonly<Record<BallSkinId, BallSkinEntry>> = deepFreeze({
  classic: { name: 'Classic', rarity: Rarity.COMMON, priceCoins: 0 },
  bubblegum: { name: 'Bubblegum', rarity: Rarity.COMMON, priceCoins: 200 },
  midnight: { name: 'Midnight', rarity: Rarity.COMMON, priceCoins: 200 },
  inferno: { name: 'Inferno', rarity: Rarity.RARE, priceCoins: 800 },
  galaxy: { name: 'Galaxy', rarity: Rarity.RARE, priceCoins: 800 },
  gold: { name: 'Gold Rush', rarity: Rarity.RARE, priceCoins: 800 },
  frostbite: { name: 'Frostbite', rarity: Rarity.EPIC, priceCoins: 2500 },
  voltage: { name: 'Voltage', rarity: Rarity.EPIC, priceCoins: 2500 },
  starfall: { name: 'Starfall', rarity: Rarity.LEGENDARY, priceCoins: 8000 },
  egg: { name: 'The Egg', rarity: Rarity.LEGENDARY, priceCoins: 10000 },
});

export const ballSkinCatalogFor = (id: BallSkinId): BallSkinEntry => BALL_SKIN_CATALOG[id];
export const isStarterBallSkin = (id: BallSkinId): boolean => ballSkinCatalogFor(id).priceCoins === 0;
export const isBallSkinId = (v: unknown): v is BallSkinId =>
  typeof v === 'string' && (BALL_SKIN_IDS as readonly string[]).includes(v);

// ── Match coin rewards (handegg-roster.md "Pricing → Coins earned per match") ──────────────────────────────
export const MATCH_REWARDS = deepFreeze({
  bot: {
    win: 10,
    loss: 3,
    /** Anti-farm cap: only the first N bot matches a day earn coins. */
    dailyCap: 20,
    /** A match shorter than this earns nothing (stops a 1-tap forfeit loop from farming the cap). */
    minDurationSec: 45,
  },
  online: {
    win: 40,
    loss: 12,
    /** +5 coins per consecutive win, capped at +20 (so a streak tops out at win + 20 = 60). */
    streakBonusPerWin: 5,
    streakBonusMax: 20,
    /** Flat bonus added to the FIRST online win of the day (once balance-regardless of streak). */
    firstWinOfDayBonus: 50,
    /** A forfeit win (opponent disconnected) pays half of the win + streak reward; the daily-first-win bonus is
     *  unaffected (it rewards logging in and winning once that day, not the quality of this particular win). */
    forfeitWinMul: 0.5,
  },
} as const);

// ── Coin packs (design/exports/store.png approved mockup; COMING SOON — not purchasable yet, no RevenueCat SDK) ──
export interface CoinPackEntry {
  readonly id: string;
  readonly name: string;
  readonly usd: number;
  /** ₪ (ILS, VAT included) — the mockup's displayed price; App Store/Play tier, not charged anywhere yet. */
  readonly ils: number;
  readonly coins: number;
  /** Bonus percent vs the base rate (500 coins/$1), display only. */
  readonly bonusPct: number;
  readonly badge?: 'POPULAR' | 'BEST VALUE';
}

export const COIN_PACKS: readonly CoinPackEntry[] = deepFreeze([
  { id: 'pocket', name: 'POCKET', usd: 0.99, ils: 3.9, coins: 500, bonusPct: 0 },
  { id: 'bag', name: 'BAG', usd: 4.99, ils: 19.9, coins: 2800, bonusPct: 12, badge: 'POPULAR' },
  { id: 'chest', name: 'CHEST', usd: 9.99, ils: 39.9, coins: 6000, bonusPct: 20 },
  { id: 'vault', name: 'VAULT', usd: 19.99, ils: 79.9, coins: 13000, bonusPct: 30 },
  { id: 'hall_of_fame', name: 'HALL OF FAME', usd: 49.99, ils: 199.9, coins: 35000, bonusPct: 40, badge: 'BEST VALUE' },
]);
